import { Device, Call } from '@twilio/voice-sdk';
import { callFunction } from './supabase';

export interface TokenResponse {
  token: string;
  identity: string;
}

let device: Device | null = null;
let activeCall: Call | null = null;
// In-flight init, so a second Start-click while we're still registering reuses
// the same attempt instead of racing a second Device/token fetch — two
// concurrent attempts each failing independently looked like "zwei
// Fehlermeldungen" with no call ever placed (Sharina-Feedback Juli 2026).
let initPromise: Promise<Device> | null = null;
// Das Gerät, das GERADE aufgebaut wird. Wird gebraucht, um zwei Fälle
// auseinanderzuhalten: Fehler während der Anmeldung (die gehören angezeigt)
// und Fehler von längst verworfenen Geräten (die gehören ignoriert).
let initialisingDevice: Device | null = null;
// Zählt jedes teardown() mit. Ein Anmeldeversuch, der währenddessen läuft,
// darf danach nicht doch noch ein Gerät aktiv setzen.
let generation = 0;

// Gehört dieses Gerät zur aktuellen Sitzung — oder ist es ein Überbleibsel?
function istAktuell(d: Device): boolean {
  return d === device || d === initialisingDevice;
}

// Device-level SDK errors (e.g. rejected registration, media/ICE failures)
// only went to console.warn before — invisible once the promise chain above
// had already resolved. Surfaced via this listener so the dialer store can
// show it in the UI instead of silently swallowing it.
type DeviceErrorListener = (msg: string) => void;
let deviceErrorListener: DeviceErrorListener | null = null;
export function onDeviceError(cb: DeviceErrorListener): void {
  deviceErrorListener = cb;
}

export async function getVoiceToken(): Promise<TokenResponse> {
  return callFunction<TokenResponse>('twilio-token');
}

// Zeitlimit für die Anmeldung an der Telefonanlage. Die läuft über eine eigene
// WebSocket-Verbindung zu Twilio, nicht über unsere Edge Functions — deshalb
// ein eigenes Budget. Ohne Zeitlimit tat der Start-Knopf stillschweigend nichts
// mehr, bis man die Seite neu geladen hat.
const REGISTER_TIMEOUT_MS = 12_000;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(
      () => reject(new Error(`${label} — bitte erneut auf Start klicken.`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); });
  });
}

export async function ensureDevice(): Promise<Device> {
  if (device && device.state !== 'destroyed') return device;
  if (initPromise) return initPromise;
  const meineGeneration = generation;
  initPromise = (async () => {
    let dev: Device | null = null;
    try {
      // Kein eigenes Zeitlimit mehr um die Token-Anfrage: das Budget steckt in
      // callFunction (s. dort) und bricht die Anfrage dort auch wirklich ab.
      // Das vorherige 12-s-Limit hat Antworten verworfen, die noch unterwegs
      // waren, und die aufgegebene Anfrage trotzdem weiterlaufen lassen.
      const { token } = await getVoiceToken();
      const d = new Device(token, {
        logLevel: 'warn',
        closeProtection: true,
        // Opus zuerst, PCMU nur noch als Rückfall. Die Voreinstellung des SDK
        // ist umgekehrt, und PCMU bringt keinen eigenen Ausgleich für verlorene
        // Pakete mit. Anlass: Kunden hörten die Mitarbeiter bei etwa jedem
        // fünften Anruf schlecht, vor allem am Gesprächsanfang (Ticket WQ49,
        // Destiny, 24.09.2026).
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });
      dev = d;
      initialisingDevice = d;
      d.on('tokenWillExpire', async () => {
        try {
          const { token: refreshed } = await getVoiceToken();
          d.updateToken(refreshed);
        } catch (err) {
          console.error('Failed to refresh Twilio token', err);
        }
      });
      d.on('error', (e) => {
        console.error('Twilio Device error', e);
        // Ein verworfenes Gerät meldet seine Fehler oft erst mit Verzögerung
        // nach. Ohne diese Prüfung riss so ein Nachzügler die inzwischen
        // laufende Sitzung zurück auf „idle" — der Dialer war danach bis zum
        // Neuladen der Seite nicht mehr zu gebrauchen.
        if (!istAktuell(d)) return;
        deviceErrorListener?.(`Telefonie-Fehler${e.code ? ` (${e.code})` : ''}: ${e.message}`);
      });
      // Der eigene "Wählton" des Twilio-SDKs wird abgeschaltet: er läuft
      // parallel zum echten Freizeichen der Gegenstelle und klingt dadurch
      // beim Verbindungsaufbau nach Störgeräusch. Es bleibt das normale
      // Tuten (deutsches Freizeichen, siehe twilio-voice) — Sharina, Juli 2026.
      try { d.audio?.outgoing(false); } catch { /* Browser ohne Audio-API */ }
      await withTimeout(d.register(), REGISTER_TIMEOUT_MS, 'Telefonie-Verbindung dauert zu lange');
      // Wurde zwischendurch beendet (Stop), gehört dieses Gerät nicht mehr
      // hierher — sonst stünde nach dem Auflegen wieder eine offene
      // Verbindung zur Telefonanlage.
      if (meineGeneration !== generation) {
        throw new Error('Telefonie wurde zwischendurch beendet.');
      }
      device = d;
      return d;
    } catch (err) {
      // Das halbfertige Gerät gehört wirklich abgeräumt. Vorher stand hier
      // `device` — das ist an dieser Stelle noch null, es wurde also nie etwas
      // aufgeräumt. Jeder gescheiterte Versuch ließ ein Gerät mit offener
      // Verbindung zurück, und genau diese Überbleibsel haben die späteren
      // Versuche kaputtgemacht.
      try { await dev?.destroy(); } catch { /* best effort */ }
      if (device === dev) device = null;
      throw err;
    } finally {
      if (initialisingDevice === dev) initialisingDevice = null;
      initPromise = null;
    }
  })();
  return initPromise;
}

export async function dial(
  toNumber: string,
  customerId?: string,
  // Lokaler Schlüssel des Wählversuchs. Wird an die Telefonanlage
  // durchgereicht, damit deren Anruf-Zeile und die Nachbereitung aus der
  // Oberfläche zusammenfinden — auch wenn die Anruf-Kennung nie eintrifft.
  attemptKey?: string,
): Promise<Call> {
  const dev = await ensureDevice();
  // Ein hängen gebliebener alter Call ("A Call is already active") blockiert
  // sonst jeden weiteren Anruf bis zum Seiten-Reload.
  if (activeCall) {
    try { dev.disconnectAll(); } catch { /* best effort */ }
    activeCall = null;
  }
  const params: Record<string, string> = { To: toNumber };
  if (customerId) params.CustomerId = customerId;
  if (attemptKey) params.AttemptKey = attemptKey;
  const call = await dev.connect({ params });
  activeCall = call;
  call.on('disconnect', () => { if (activeCall === call) activeCall = null; });
  call.on('cancel', () => { if (activeCall === call) activeCall = null; });
  call.on('error', () => { if (activeCall === call) activeCall = null; });
  return call;
}

export function getActiveCall(): Call | null {
  return activeCall;
}

export function hangup(): void {
  activeCall?.disconnect();
  activeCall = null;
}

export function setMuted(muted: boolean): void {
  activeCall?.mute(muted);
}

export async function teardown(): Promise<void> {
  hangup();
  // Auch ein Gerät abräumen, das gerade noch aufgebaut wird: sonst bleibt
  // nach „Stop" während des Verbindungsaufbaus eine offene Verbindung zurück.
  generation++;
  const abzuräumen = [device, initialisingDevice].filter((d): d is Device => !!d);
  device = null;
  initialisingDevice = null;
  initPromise = null;
  for (const d of abzuräumen) {
    try {
      if (d.state !== 'destroyed') await d.destroy();
    } catch { /* best effort */ }
  }
}
